import { useState, useEffect, useMemo } from 'react'

const useGdpData = () => {
    const [gdpData, setGdpData] = useState(new Map())
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        const fetchGdpData = async () => {
            try {
                setLoading(true)
                setError(null)
                
                const currentYear = new Date().getFullYear()
                const previousYear = currentYear - 1
                
                const response = await fetch(
                    `https://api.worldbank.org/v2/country/all/indicator/NY.GDP.PCAP.CD?format=json&per_page=20000&date=${previousYear}:${currentYear}`
                )
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                
                const data = await response.json()
                
                if (!Array.isArray(data) || data.length < 2) {
                    throw new Error('Invalid API response format')
                }
                
                const gdpEntries = data[1] || []
                const gdpMap = new Map()
                
                gdpEntries.forEach(entry => {
                    if (entry.value !== null && entry.countryiso3code) {
                        const existingEntry = gdpMap.get(entry.countryiso3code)
                        if (!existingEntry || (entry.date > existingEntry.date)) {
                            gdpMap.set(entry.countryiso3code, {
                                value: entry.value,
                                date: entry.date,
                                countryName: entry.country.value
                            })
                        }
                    }
                })
                
                setGdpData(gdpMap)
            } catch (err) {
                setError(err.message)
            } finally {
                setLoading(false)
            }
        }

        fetchGdpData()
    }, [])

    const formatGdp = useMemo(() => {
        return (value) => {
            if (value === null || value === undefined) return null
            
            const numValue = Number(value)
            if (isNaN(numValue)) return null
            
            // Format GDP per capita with comma separators
            return `$${numValue.toLocaleString('en-US', { 
                maximumFractionDigits: 0 
            })}`
        }
    }, [])

    const getGdpByIso3 = useMemo(() => {
        return (iso3Code) => {
            const entry = gdpData.get(iso3Code)
            return entry ? entry.value : null
        }
    }, [gdpData])

    return {
        gdpData,
        loading,
        error,
        formatGdp,
        getGdpByIso3
    }
}

export default useGdpData